import { useCallback } from 'react';

import { GQLResponsePerimeterXChallengeError } from '@change-corgi/core/gql';
import { useI18n } from '@change-corgi/core-react/i18n';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { useLoginOrSignupPageData, useLoginOrSignupPageState } from 'src/app/pages/loginOrSignup/pageContext';

import { useLoginOrSignup } from '../../../useLoginOrSignup';

import { useAuthMethodPerimeterXCaptcha } from './useAuthMethodPerimeterXCaptcha';

type GotoLoginOrSignupHook = () => Promise<void>;

export function useGotoLoginOrSignup(): GotoLoginOrSignupHook {
	const [state, { setLoading, setSuccess, setError, gotoStep, setAuthMethod }] = useLoginOrSignupPageState();
	const { getEmailAuthMethod } = useLoginOrSignup();
	const utilityContext = useUtilityContext();
	const { translate } = useI18n();
	const triggerPerimeterXCaptcha = useAuthMethodPerimeterXCaptcha();
	const {
		data: { experiments },
	} = useLoginOrSignupPageData();
	const isOtpV1 = experiments.corgiOtp.variation === 'v1';
	const isOtpV2 = experiments.corgiOtp.variation === 'v2';

	const gotoLoginOrSignup = useCallback(async () => {
		setLoading();
		async function attempt() {
			try {
				const authMethod = await getEmailAuthMethod(state.email, utilityContext);
				setSuccess();
				setAuthMethod(authMethod);
				if (authMethod === 'INVALID_EMAIL') {
					// TODO update error with a clearer message explaining what could be wrong with the email address
					setError(translate('fe.errors.user.email.prefix'));
				} else if (authMethod === 'UNKNOWN_USER') {
					gotoStep('signup', { canGoBack: true });
				} else if (isOtpV1) {
					gotoStep('otp', { canGoBack: true });
				} else if (isOtpV2 && authMethod === 'NO_PASSWORD_SET') {
					gotoStep('otp', { canGoBack: true });
				} else if (authMethod === 'NO_PASSWORD_SET') {
					gotoStep('no_password', { canGoBack: true });
				} else {
					gotoStep('login', { canGoBack: true }); // 'PASSWORD_SET' (control and v2)
				}
			} catch (error: unknown) {
				if (error instanceof GQLResponsePerimeterXChallengeError) {
					await triggerPerimeterXCaptcha({
						pxResponse: error.pxChallenge,
					});
					await attempt();
				} else {
					const loginError = typeof error === 'string' ? error : translate('corgi.login_or_signup.error.network');
					setError(loginError);
				}
			}
		}
		await attempt();
	}, [
		setLoading,
		setSuccess,
		setError,
		gotoStep,
		setAuthMethod,
		getEmailAuthMethod,
		state.email,
		utilityContext,
		translate,
		triggerPerimeterXCaptcha,
		isOtpV1,
		isOtpV2,
	]);

	return gotoLoginOrSignup;
}
